import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import { APPROVE_STATUS } from '../constants/product.constants';
import type { DistributionMemberParams, SpecimenDistributionSearch } from '../types/product.types';
import { toDistributionFilterSearch, toDistributionFilterValues } from '../utils/productFilters';

const ROUTES = {
  distribution: getRouteApi('/_auth/product/specimen-distribution/'),
  adjustment: getRouteApi('/_auth/product/specimen-adjustment'),
} as const;

export type DistributionRouteId = keyof typeof ROUTES;

const DEFAULT_APPROVE_TYPE_NO: Record<DistributionRouteId, number | undefined> = {
  distribution: APPROVE_STATUS.APPROVED,
  adjustment: undefined,
};

const normalizeFilterValues = (values: Record<string, string>) =>
  toDistributionFilterValues(toDistributionFilterSearch(values));

export function useDistributionFilters(routeId: DistributionRouteId) {
  const route = ROUTES[routeId];
  const rawSearch = route.useSearch();
  const navigate = route.useNavigate();
  const defaultApproveTypeNo = DEFAULT_APPROVE_TYPE_NO[routeId];

  const search = useMemo(
    (): SpecimenDistributionSearch =>
      rawSearch.approveTypeNo === undefined && defaultApproveTypeNo !== undefined
        ? { ...rawSearch, approveTypeNo: defaultApproveTypeNo }
        : rawSearch,
    [rawSearch, defaultApproveTypeNo],
  );

  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const defaultValues = useMemo(
    () =>
      defaultApproveTypeNo === undefined
        ? defaultPeriod
        : { ...defaultPeriod, approveTypeNo: String(defaultApproveTypeNo) },
    [defaultPeriod, defaultApproveTypeNo],
  );
  const appliedValues = useMemo(() => toDistributionFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toDistributionFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const bookSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): SpecimenDistributionSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
    }),
    [search, defaultPeriod],
  );

  const drillParams = useMemo<DistributionMemberParams>(
    () => ({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      userId: params.userId,
      divisionId: params.divisionId,
      districtId: params.districtId,
      thanaId: params.thanaId,
      companyId: params.companyId,
      departmentId: params.departmentId,
      approveTypeNo: params.approveTypeNo,
    }),
    [params],
  );

  return {
    ...filters,
    ...bookSearch,
    params,
    drillParams,
  };
}
